import { useMemo } from 'react';
import cm from 'classnames';

import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import { Path, useAppNavigator, useLocation } from 'components/_hooks/use-router';
import { passHelpMode, setTutorials, setIntro } from 'components/app/_mediator/app.slice';
import { selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { selectFiles } from 'components/app/_mediator/app-files.slice';
import { IntroKeys } from 'components/app/intro/intro-keys';

import { ReactComponent as ArrowIcon } from 'assets/nav-pane/arrow.svg';
import { ReactComponent as IntroIcon } from 'assets/logo.svg';
import { ReactComponent as ProjectsIcon } from 'assets/nav-pane/projects.svg';
import { ReactComponent as ClipSelectorIcon } from 'assets/nav-pane/clip-selector.svg';
import { ReactComponent as StoryboardIcon } from 'assets/nav-pane/storyboard.svg';
import { ReactComponent as FinalCutsIcon } from 'assets/nav-pane/final-сuts.svg';

import { TooltipHelp } from 'components/UI/tooltip/tooltip-help';
import { Tooltip } from 'components/UI/tooltip/tooltip';
import { AppButton } from 'components/app/app-components/app-button/app-button';
import styles from '../_app-container.module.scss';

export const AppNavigation = () => {
  const { path } = useLocation();
  const { isAudio, isVideo, isImage, brokenFiles } = useAppSelector(selectFiles);
  const finalcutsAvailable = useAppSelector((state) => state.app.finalcutsAvailable);
  const editorAvailable = useAppSelector((state) => state.app.editorAvailable);
  const stories = useAppSelector((state) => state.app__data.stories);
  const tutorials = useAppSelector((state) => state.app.tutorials);
  const user = useAppSelector((state) => state.app__data.user);
  const helpMode = useAppSelector(selectHelpMode);
  const dispatch = useAppDispatch();

  const EditorTips = useMemo(() => {
    const editor: string[] = [];
    !isAudio && editor.push('No Available Audio file');
    !(isVideo || isImage) && editor.push('No Available Video or Image file');
    !stories?.length && editor.push('No created Story');
    !!brokenFiles.length && editor.push(`${brokenFiles[0].fileName} — file duration or size is 0`);
    return editor;
  }, [isAudio, isVideo, isImage, brokenFiles, stories?.length]);

  const introKey = useMemo(() => {
    switch (path) {
      case Path.pulseIntro:
        return 'Intro';
      case Path.projects:
        return 'Projects';
      case Path.selector:
        return 'Highlights';
      case Path.storyboard:
        return 'Storyboard';
      case Path.finalcuts:
        return 'Final Cuts';
    }
  }, [path]);

  return (
    <div className={styles.Navigation} data-intro={IntroKeys.navigator}>
      {helpMode && (
        <NavLink el={{ path: Path.pulseIntro, title: 'Intro', ico: <IntroIcon />, introId: IntroKeys.intro }} />
      )}
      <NavLink el={{ path: Path.projects, title: 'Projects', ico: <ProjectsIcon />, introId: IntroKeys.navProjects }} />
      <NavLink
        el={{ path: Path.selector, title: 'Highlights', ico: <ClipSelectorIcon />, introId: IntroKeys.navSr }}
        disabled={!editorAvailable}
        tooltips={EditorTips}
      />
      <NavLink
        el={{ path: Path.storyboard, title: 'Storyboard', ico: <StoryboardIcon />, introId: IntroKeys.navSb }}
        disabled={!editorAvailable}
        tooltips={EditorTips}
      />
      <NavLink
        el={{ path: Path.finalcuts, title: 'Final Cuts', ico: <FinalCutsIcon />, introId: IntroKeys.navFc }}
        tooltips={!finalcutsAvailable ? ['No created Final cut'] : []}
        disabled={!finalcutsAvailable}
      />
      {user && (
        <footer>
          {helpMode && (
            <>
              <AppButton
                className={cm(styles.Navigation__helpMode, { [styles['Navigation__helpMode--disabled']]: !tutorials })}
                onClick={() => dispatch(setTutorials(!tutorials))}
                data-intro={IntroKeys.navTutorials}
                tip={
                  <ul>
                    {tutorials
                      ? 'Help is Enabled - click on highlighted components for additional help and tutorials'
                      : 'Help is Disabled - click to enable component tutorials'}
                  </ul>
                }
                tipPosition="right"
                showTooltip={true}
              >
                ❔
              </AppButton>
              <AppButton
                className={styles.Navigation__intro}
                onClick={() => dispatch(setIntro(true))}
                data-intro={IntroKeys.navStartTour}
                tip={`Start ${introKey} tour`}
                tipPosition="right"
              >
                Start Tour
              </AppButton>
            </>
          )}
          <AppButton
            className={cm(styles.Navigation__helpMode, { [styles['Navigation__helpMode--disabled']]: !helpMode })}
            onClick={() => dispatch(passHelpMode(!helpMode))}
            data-intro={IntroKeys.navHelpMode}
            tip={
              <ul>
                {helpMode ? 'Disable ' : 'Enable '}
                <strong>Help Mode</strong> (tooltips & tutorials)
              </ul>
            }
            tipPosition="right"
            showTooltip={true}
          >
            Help Mode
          </AppButton>
        </footer>
      )}
    </div>
  );
};

/* C O M P O N E N T S */

type NavLinkProps = {
  el: {
    path: Path;
    title: string;
    ico: JSX.Element;
    introId: IntroKeys;
  };
  tooltips?: string[];
  disabled?: boolean;
};

const NavLink = ({ el, tooltips, disabled }: NavLinkProps) => {
  const helpMode = useAppSelector(selectHelpMode);
  const { navigateTo } = useAppNavigator();
  const { path } = useLocation();

  return (
    <div
      data-intro={el.introId}
      className={cm(styles.Navigation__step, {
        [styles.disabled]: disabled,
      })}
    >
      <Tooltip
        className={styles.Navigation__stepTooltip}
        showTooltip={helpMode && disabled}
        position="right"
        tip={
          tooltips && (
            <TooltipHelp>
              {tooltips.map((el) => (
                <p key={el}>{el}</p>
              ))}
            </TooltipHelp>
          )
        }
      >
        <ArrowIcon className={styles.Navigation__stepArrow} />
        <div
          onClick={() => navigateTo(el.path)}
          className={cm(styles.Navigation__stepLink, {
            [styles.active]: path === el.path,
          })}
        >
          {el.ico}
          <span>{el.title}</span>
        </div>
      </Tooltip>
    </div>
  );
};
